import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Bot, LogOut, User, Users, Settings, Wand2, Activity, Bell, FlaskConical, Gamepad2, Workflow, Calendar, ChevronLeft, ChevronRight, BarChart3, FileText, Tags, TrendingUp, MessageSquare } from 'lucide-react'
import clsx from 'clsx'
import { useI18n } from '@/i18n'
import type { NavigationMessages } from '@/i18n/messages'

interface NavItem {
  nameKey: keyof NavigationMessages
  path: string
  icon: typeof Bot
}

const navItems: NavItem[] = [
  { nameKey: 'agentConfigs', path: '/', icon: Bot },
  { nameKey: 'systemSettings', path: '/system-settings', icon: Settings },
  { nameKey: 'promptTemplates', path: '/prompt-templates', icon: FileText },
  { nameKey: 'skills', path: '/skills', icon: Wand2 },
  { nameKey: 'monitoring', path: '/monitoring', icon: Activity },
  { nameKey: 'reports', path: '/reports', icon: BarChart3 },
  { nameKey: 'classificationAnalytics', path: '/classification-analytics', icon: TrendingUp },
  { nameKey: 'messageFeedback', path: '/message-feedback', icon: MessageSquare },
  { nameKey: 'users', path: '/users', icon: Users },
  { nameKey: 'pushTest', path: '/push-test', icon: Bell },
  { nameKey: 'evaluation', path: '/evaluation', icon: FlaskConical },
  { nameKey: 'playground', path: '/playground', icon: Gamepad2 },
  { nameKey: 'imageWorkflows', path: '/image-workflows', icon: Workflow },
  { nameKey: 'workflowSchedules', path: '/workflow-schedules', icon: Calendar },
  { nameKey: 'annotation', path: '/annotation/batches', icon: Tags },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { messages } = useI18n()
  const t = messages.navigation

  const username = localStorage.getItem('admin_username') || localStorage.getItem('admin_email') || 'Admin'

  const handleLogout = async () => {
    // Clear all auth data
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user_id')
    localStorage.removeItem('admin_email')
    localStorage.removeItem('admin_username')
    sessionStorage.removeItem('admin_token')

    // Navigate to login
    navigate('/login', { replace: true })
  }

  return (
    <aside className={clsx(
      'bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-300',
      collapsed ? 'w-16' : 'w-64'
    )}>
      {/* Logo & Toggle */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
        {!collapsed && (
          <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t.appTitle}
          </h1>
        )}
        <button
          onClick={onToggle}
          className={clsx(
            'p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors',
            collapsed && 'mx-auto'
          )}
          title={collapsed ? t.expandSidebar : t.collapseSidebar}
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon
          const itemName = t[item.nameKey]
          const isActive = location.pathname === item.path ||
            (item.path === '/' && location.pathname.startsWith('/agents')) ||
            (item.path !== '/' && location.pathname.startsWith(item.path))
          const isEvaluation = item.path === '/evaluation'

          return (
            <Link
              key={item.path}
              to={item.path}
              className={clsx(
                'flex items-center gap-3 px-3 py-3 rounded-lg transition-colors',
                isActive
                  ? isEvaluation
                    ? 'bg-violet-50 text-violet-700 font-medium'
                    : 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100',
                collapsed && 'justify-center'
              )}
              title={collapsed ? itemName : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span>{itemName}</span>}
            </Link>
          )
        })}
      </nav>

      {/* User & Logout */}
      <div className="p-2 border-t border-gray-200">
        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2 text-gray-700">
            <User className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm truncate flex-1">{username}</span>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={clsx(
            'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors',
            collapsed && 'justify-center'
          )}
          title={collapsed ? t.logout : undefined}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>{t.logout}</span>}
        </button>
      </div>
    </aside>
  )
}
